import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Lock, Loader2, ArchiveRestore, Check, X, ListChecks, ClipboardPaste } from "lucide-react";
import { invalidateJobStatuses, fetchJobStatuses } from "../../hooks/useJobStatuses";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const CATEGORY_META = {
  active: { label: "Active", desc: "Open job (like Scheduled)", cls: "bg-blue-500/10 text-blue-500" },
  completed: { label: "Completed", desc: "Counts as completed in reports & revenue", cls: "bg-emerald-500/10 text-emerald-500" },
  cancelled: { label: "Cancelled", desc: "Counts as cancelled", cls: "bg-red-500/10 text-red-500" },
};

const CategoryBadge = ({ category }) => {
  const m = CATEGORY_META[category] || CATEGORY_META.active;
  return <Badge className={`${m.cls} border-0 text-[10px]`}>{m.label}</Badge>;
};

export default function JobStatusesTab() {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", color: "", category: "active" });
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ label: "", color: "#06b6d4", category: "active" });
  const [forbidden, setForbidden] = useState(false);
  const [pasteDefault, setPasteDefault] = useState("new-lead");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/job-statuses`, auth());
      setStatuses(r.data?.statuses || []);
      try {
        const b = await axios.get(`${API}/api/settings/business`, auth());
        setPasteDefault(b.data?.paste_ticket_default_status || "new-lead");
      } catch (_) {}
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error("Failed to load job statuses");
    } finally {
      setLoading(false);
    }
  }, []);

  const savePasteDefault = async (statusId) => {
    const prev = pasteDefault;
    setPasteDefault(statusId);
    try {
      await axios.put(`${API}/api/settings/business`, { paste_ticket_default_status: statusId }, auth());
      toast.success("Paste Ticket default status saved");
    } catch (e) {
      setPasteDefault(prev);
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  useEffect(() => { load(); }, [load]);

  const refreshEverywhere = async () => {
    invalidateJobStatuses();
    await Promise.all([load(), fetchJobStatuses("owner", true)]);
  };

  const apiCall = async (fn, okMsg) => {
    setSaving(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await refreshEverywhere();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addStatus = () => apiCall(
    () => axios.post(`${API}/api/job-statuses`, addForm, auth()),
    "Status added"
  ).then((ok) => { if (ok) { setAddOpen(false); setAddForm({ label: "", color: "#06b6d4", category: "active" }); } });

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({ label: s.label, color: s.color, category: s.category });
  };

  const saveEdit = (s) => {
    const body = { label: editForm.label, color: editForm.color };
    if (!s.system) body.category = editForm.category;
    return apiCall(
      () => axios.patch(`${API}/api/job-statuses/${s.id}`, body, auth()),
      "Status updated"
    ).then((ok) => { if (ok) setEditingId(null); });
  };

  const deleteStatus = (s) => {
    if (!window.confirm(`Delete "${s.label}"? If jobs use it, it will be archived instead.`)) return;
    apiCall(async () => {
      const r = await axios.delete(`${API}/api/job-statuses/${s.id}`, auth());
      if (r.data?.archived) toast.info(`"${s.label}" is used by ${r.data.jobs_using} job(s) — archived instead of deleted`);
    }, null);
  };

  const restoreStatus = (s) =>
    apiCall(() => axios.patch(`${API}/api/job-statuses/${s.id}`, { archived: false }, auth()), `"${s.label}" restored`);

  const move = (idx, dir) => {
    const act = statuses.filter((s) => !s.archived);
    const target = idx + dir;
    if (target < 0 || target >= act.length) return;
    const ids = act.map((s) => s.id);
    [ids[idx], ids[target]] = [ids[target], ids[idx]];
    apiCall(() => axios.put(`${API}/api/job-statuses/reorder`, { order: ids }, auth()), null);
  };

  if (forbidden) return (
    <Card data-testid="job-statuses-card"><CardContent className="py-10 text-center text-sm text-muted-foreground">
      Only the account owner or an admin can manage job statuses.
    </CardContent></Card>
  );

  const active = statuses.filter((s) => !s.archived);
  const archived = statuses.filter((s) => s.archived);

  return (
    <Card data-testid="job-statuses-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg"><ListChecks className="w-5 h-5" /> Job Statuses</CardTitle>
            <CardDescription className="mt-1">
              Rename, recolor and reorder statuses, or add your own. Built-in statuses can't be deleted.
              Custom statuses with the "Completed" category count toward completed jobs and revenue.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setAddOpen(!addOpen)} data-testid="add-status-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Status
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Paste Ticket default status */}
        <div className="p-3 rounded-xl border border-border bg-muted/30 flex flex-wrap items-center justify-between gap-3" data-testid="paste-ticket-default-row">
          <div className="min-w-[220px]">
            <p className="text-sm font-medium flex items-center gap-2">
              <ClipboardPaste className="w-4 h-4 text-muted-foreground" /> Paste Ticket default status
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Status applied to jobs created via Paste Ticket. Tickets pasted with a closing amount (e.g. "T: $600") still prefill as Completed.
            </p>
          </div>
          <Select value={pasteDefault} onValueChange={savePasteDefault}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="paste-ticket-default-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {active.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {addOpen && (
          <div className="p-3 rounded-xl border border-border bg-muted/30 flex flex-wrap items-end gap-3" data-testid="add-status-form">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-muted-foreground">Name</label>
              <Input value={addForm.label} onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                placeholder="e.g. Awaiting Parts" className="mt-1 h-9" data-testid="new-status-name" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Color</label>
              <input type="color" value={addForm.color} onChange={(e) => setAddForm({ ...addForm, color: e.target.value })}
                className="block mt-1 h-9 w-14 rounded-md border border-border bg-transparent cursor-pointer" data-testid="new-status-color" />
            </div>
            <div className="min-w-[170px]">
              <label className="text-xs text-muted-foreground">Lifecycle</label>
              <Select value={addForm.category} onValueChange={(v) => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger className="mt-1 h-9" data-testid="new-status-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label} — <span className="text-muted-foreground text-xs">{m.desc}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={addStatus} disabled={saving || !addForm.label.trim()} data-testid="save-new-status-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-1.5">
            {active.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2.5 p-2.5 rounded-xl border border-border bg-card" data-testid={`status-row-${s.id}`}>
                <div className="flex flex-col">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0 || saving} className="text-muted-foreground hover:text-foreground disabled:opacity-20" data-testid={`move-up-${s.id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === active.length - 1 || saving} className="text-muted-foreground hover:text-foreground disabled:opacity-20" data-testid={`move-down-${s.id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
                {editingId === s.id ? (
                  <>
                    <input type="color" value={editForm.color} onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                      className="h-8 w-10 rounded-md border border-border bg-transparent cursor-pointer shrink-0" data-testid={`edit-color-${s.id}`} />
                    <Input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                      className="h-8 flex-1 min-w-[120px]" data-testid={`edit-name-${s.id}`} />
                    {!s.system && (
                      <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                        <SelectTrigger className="h-8 w-[130px]" data-testid={`edit-category-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(CATEGORY_META).map(([k, m]) => (
                            <SelectItem key={k} value={k}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-500" onClick={() => saveEdit(s)} disabled={saving} data-testid={`save-edit-${s.id}`}><Check className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                  </>
                ) : (
                  <>
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="text-sm font-medium flex-1 truncate">{s.label}</span>
                    <CategoryBadge category={s.category} />
                    {s.system && <Badge variant="outline" className="text-[10px] text-muted-foreground gap-1"><Lock className="w-2.5 h-2.5" /> Built-in</Badge>}
                    <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => startEdit(s)} data-testid={`edit-status-${s.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {!s.system && (
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-red-400 hover:text-red-500" onClick={() => deleteStatus(s)} data-testid={`delete-status-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {archived.length > 0 && (
          <div className="pt-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Archived</p>
            <div className="space-y-1.5">
              {archived.map((s) => (
                <div key={s.id} className="flex items-center gap-2.5 p-2.5 rounded-xl border border-dashed border-border opacity-70" data-testid={`archived-row-${s.id}`}>
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-sm flex-1 truncate line-through">{s.label}</span>
                  <CategoryBadge category={s.category} />
                  <Button size="sm" variant="ghost" className="h-8 px-2 text-blue-400" onClick={() => restoreStatus(s)} data-testid={`restore-status-${s.id}`}>
                    <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Archived statuses stay visible on existing jobs but can't be assigned to new ones.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
